const REFERENCE_YEAR = 2024;
const REFERENCE_MONTH_INDEX = 6; // July to support all 31-day schedules

export const BILLING_SOURCE_TIMEZONE = 'America/Los_Angeles'

const TIMEZONE_LABEL_MAP: Record<string, string> = {
  "Etc/GMT+0": "UTC",
  "Etc/GMT+1": "UTC-1",
  "Etc/GMT+2": "UTC-2",
  "Etc/GMT+3": "UTC-3",
  "Etc/GMT+4": "UTC-4",
  "Etc/GMT+5": "UTC-5",
  "Etc/GMT+6": "UTC-6",
  "Etc/GMT+7": "UTC-7",
  "Etc/GMT+8": "UTC-8",
  "Etc/GMT+9": "UTC-9",
  "Etc/GMT+10": "UTC-10",
  UTC: "UTC",
};

const TIMEZONE_ALIASES: Record<string, string> = {
  "UTC-12": "Etc/GMT+12",
  "UTC-11": "Etc/GMT+11",
  "UTC-10": "Etc/GMT+10",
  "UTC-9": "Etc/GMT+9",
  "UTC-8": "Etc/GMT+8",
  "UTC-7": "Etc/GMT+7",
  "UTC-6": "Etc/GMT+6",
  "UTC-5": "Etc/GMT+5",
  "UTC-4": "Etc/GMT+4",
  "UTC-3": "Etc/GMT+3",
  "UTC-2": "Etc/GMT+2",
  "UTC-1": "Etc/GMT+1",
  UTC: "Etc/UTC",
  "UTC+1": "Etc/GMT-1",
  "UTC+2": "Etc/GMT-2",
  "UTC+3": "Etc/GMT-3",
  "UTC+4": "Etc/GMT-4",
  "UTC+5": "Etc/GMT-5",
  "UTC+6": "Etc/GMT-6",
  "UTC+7": "Etc/GMT-7",
  "UTC+8": "Etc/GMT-8",
  "UTC+9": "Etc/GMT-9",
  "UTC+10": "Etc/GMT-10",
  "UTC+11": "Etc/GMT-11",
  "UTC+12": "Etc/GMT-12",
};

const resolveOptionalTimezone = (timezone?: string | null): string | null => {
  if (!timezone || typeof timezone !== "string") {
    return null;
  }

  const trimmed = timezone.trim();
  if (!trimmed) {
    return null;
  }

  const candidate = TIMEZONE_ALIASES[trimmed] || trimmed;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate });
    return candidate;
  } catch {
    return null;
  }
};

interface LocalizedScheduleInfo {
  localDay: number;
  localMonth: number;
  localTime: string;
  localDate: Date;
  localTimeZone: string;
  label: string;
}

const getTimeZoneOffset = (date: Date, timeZone: string): number => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date);
  const values: Record<string, string> = {};
  for (const { type, value } of parts) {
    if (type !== "literal") {
      values[type] = value;
    }
  }

  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );

  return (asUtc - date.getTime()) / 60000;
};

export const getUserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const parseTime = (
  billTime: string
): { hour: number; minute: number } | null => {
  const [hourStr, minuteStr] = billTime.split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  return { hour, minute };
};

export const getLocalizedScheduleInfo = (
  billDate: number,
  billTime: string,
  sourceTimeZone?: string
): LocalizedScheduleInfo | null => {
  const parsedTime = parseTime(billTime);
  if (!parsedTime) {
    return null;
  }

  const timeZone = sourceTimeZone?.trim() || BILLING_SOURCE_TIMEZONE;
  const baseDate = new Date(
    Date.UTC(
      REFERENCE_YEAR,
      REFERENCE_MONTH_INDEX,
      billDate,
      parsedTime.hour,
      parsedTime.minute
    )
  );

  try {
    const sourceOffset = getTimeZoneOffset(baseDate, timeZone);
    const utcDate = new Date(baseDate.getTime() - sourceOffset * 60000);

    const userTimeZone = getUserTimeZone();
    const localFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: userTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

    const localParts = localFormatter.formatToParts(utcDate);
    const values: Record<string, string> = {};
    for (const { type, value } of localParts) {
      if (type !== "literal") {
        values[type] = value;
      }
    }

    const localDay = Number(values.day);
    const localMonth = Number(values.month);
    const localTime = `${values.hour}:${values.minute}`;

    return {
      localDay,
      localMonth,
      localTime,
      localDate: utcDate,
      localTimeZone: userTimeZone,
      label: `Day ${localDay} at ${localTime}`,
    };
  } catch {
    return null;
  }
};

export const getTimezoneDisplayName = (timeZone?: string): string => {
  if (!timeZone) {
    return "UTC";
  }

  return TIMEZONE_LABEL_MAP[timeZone] || timeZone;
};

export const convertLocalScheduleToSource = (
  localDay: number,
  localTime: string,
  sourceTimeZone: string = BILLING_SOURCE_TIMEZONE,
  clientTimeZone?: string
): { billDate: number; billTime: string } | null => {
  const parsedTime = parseTime(localTime);
  if (!parsedTime) {
    return null;
  }

  const timezone = clientTimeZone?.trim() || getUserTimeZone();
  const baseDate = new Date(
    Date.UTC(
      REFERENCE_YEAR,
      REFERENCE_MONTH_INDEX,
      localDay,
      parsedTime.hour,
      parsedTime.minute
    )
  );

  try {
    const localOffset = getTimeZoneOffset(baseDate, timezone);
    const utcDate = new Date(baseDate.getTime() - localOffset * 60000);

    const sourceFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: sourceTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

    const sourceParts = sourceFormatter.formatToParts(utcDate);
    const values: Record<string, string> = {};
    for (const { type, value } of sourceParts) {
      if (type !== "literal") {
        values[type] = value;
      }
    }

    return {
      billDate: Number(values.day),
      billTime: `${values.hour}:${values.minute}`,
    };
  } catch {
    return null;
  }
};

const toDateInstance = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const direct = new Date(trimmed);
    if (!Number.isNaN(direct.getTime())) {
      return direct;
    }
  }

  return null;
};

export const formatDateTimeForUser = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }
): string => {
  const date = toDateInstance(value);
  if (!date) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: getUserTimeZone(),
  }).format(date);
};

export const formatDateForUser = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }
): string => formatDateTimeForUser(value, options);

export const formatTimeForUser = (
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  }
): string => formatDateTimeForUser(value, options);

export const formatDateTimeInTimezone = (
  value: string | Date | null | undefined,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }
): string => {
  const date = toDateInstance(value);
  if (!date) {
    return "—";
  }

  const resolved = resolveOptionalTimezone(timezone) || BILLING_SOURCE_TIMEZONE;

  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: resolved,
  }).format(date);
};
